// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // On mount: verify any saved token is still valid before trusting it.
  // Calling /me confirms the token hasn't expired server-side.
  useEffect(() => {
    const savedToken = sessionStorage.getItem('auth_token');
    if (!savedToken) {
      setLoading(false);
      return;
    }

    api.auth.me(savedToken)
      .then((userDetails) => {
        setToken(savedToken);
        setUser(userDetails);
        // Refresh stored user data in case profile changed
        sessionStorage.setItem('auth_user', JSON.stringify(userDetails));
      })
      .catch(() => {
        // Token invalid or expired — clear silently, user can log in again
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_user');
      })
      .finally(() => setLoading(false));
  }, []);

  // When api/client.js silently refreshes the access token, update state here
  // so future calls from AuthContext (e.g. changePassword) use the new token.
  useEffect(() => {
    const handleRefreshed = (e) => {
      const newToken = e.detail?.token;
      if (newToken) setToken(newToken);
    };

    window.addEventListener('auth:token-refreshed', handleRefreshed);
    return () => window.removeEventListener('auth:token-refreshed', handleRefreshed);
  }, []);

  // Listen for auth:expired — fired after a refresh attempt also fails.
  // Handles involuntary expiry gracefully (no hard reload, game state preserved).
  useEffect(() => {
    const handleExpired = () => {
      setToken(null);
      setUser(null);
      setSessionExpired(true);
    };

    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  const login = async (email, password) => {
    const response = await api.auth.login(email, password);
    const authToken = response.access_token || response.token;

    const userDetails = await api.auth.me(authToken);

    setToken(authToken);
    setUser(userDetails);
    setSessionExpired(false);

    sessionStorage.setItem('auth_token', authToken);
    sessionStorage.setItem('auth_user', JSON.stringify(userDetails));
    // Store refresh token so api/client.js can use it cross-origin in dev
    if (response.refresh_token) {
      sessionStorage.setItem('auth_refresh', response.refresh_token);
    }

    return response;
  };

  const register = async (username, email, password) => {
    await api.auth.register(username, email, password);
    return login(email, password);
  };

  // User-initiated logout: clear cookies server-side and reload to reset all game state.
  const logout = useCallback(() => {
    api.auth.logout().catch(() => {}); // best-effort cookie clear
    setToken(null);
    setUser(null);
    setSessionExpired(false);
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_refresh');
    sessionStorage.removeItem('auth_user');
    window.location.reload(); // intentional — resets all game context state
  }, []);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const changePassword = async (oldPassword, newPassword) => {
    if (!token) throw new Error('Not authenticated');
    return api.auth.changePassword(token, oldPassword, newPassword);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    isAdmin: user?.is_admin || false,
    sessionExpired,
    clearSessionExpired,
    login,
    register,
    logout,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
